import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  CheckCircle2,
  Clipboard,
  Copy,
  ExternalLink,
  FileCode2,
  FileText,
  Globe2,
  Languages,
  LockKeyhole,
  RadioTower,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldX,
  Sparkles,
  Table2
} from "lucide-react";
import { sampleScan } from "./sampleScan";
import type { HeaderCheck, HeaderPair, HeaderScan, Level, ScanIssue } from "./types";
import "./styles.css";

type Language = "en" | "ko";
type OutputKey = "cloudflare" | "nginx" | "netlify" | "markdown";

const copyTabs: Array<{ key: OutputKey; label: string }> = [
  { key: "cloudflare", label: "Cloudflare" },
  { key: "nginx", label: "Nginx" },
  { key: "netlify", label: "Netlify" },
  { key: "markdown", label: "Report" }
];

const text = {
  en: {
    product: "HeaderHarden Studio",
    title: "Security headers scanner with paste-ready fixes",
    subtitle: "Inspect public response headers, map CSP risk, and generate Cloudflare Pages _headers, Nginx, Netlify, and Markdown output.",
    url: "Public URL",
    scan: "Scan",
    sample: "Sample",
    scanning: "Scanning",
    noLogin: "No login. Public response headers only.",
    score: "Header score",
    status: "Response",
    csp: "CSP map",
    checks: "Header checks",
    issues: "Fix queue",
    raw: "Raw headers",
    output: "Generated output",
    copy: "Copy",
    copied: "Copied",
    consoleAlt: "HeaderHarden Studio visual console concept",
    noteTitle: "Cloudflare Pages note",
    noteBody: "_headers rules apply to static asset responses. Pages Functions responses need headers set in the function code.",
    error: "The scan could not run."
  },
  ko: {
    product: "HeaderHarden Studio",
    title: "복사 가능한 수정안을 주는 보안 헤더 검사기",
    subtitle: "공개 응답 헤더와 CSP 위험을 점검하고 Cloudflare Pages _headers, Nginx, Netlify, Markdown 출력을 생성합니다.",
    url: "공개 URL",
    scan: "검사",
    sample: "샘플",
    scanning: "검사 중",
    noLogin: "로그인 없이 공개 응답 헤더만 읽습니다.",
    score: "헤더 점수",
    status: "응답",
    csp: "CSP 맵",
    checks: "헤더 점검",
    issues: "수정 대기열",
    raw: "원본 헤더",
    output: "생성 출력",
    copy: "복사",
    copied: "복사됨",
    consoleAlt: "HeaderHarden Studio 시각 콘솔 콘셉트",
    noteTitle: "Cloudflare Pages 참고",
    noteBody: "_headers 규칙은 정적 에셋 응답에 적용됩니다. Pages Functions 응답은 함수 코드에서 헤더를 설정해야 합니다.",
    error: "검사를 실행하지 못했습니다."
  }
};

function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [targetUrl, setTargetUrl] = useState("https://example.com");
  const [scan, setScan] = useState<HeaderScan>(sampleScan);
  const [activeOutput, setActiveOutput] = useState<OutputKey>("cloudflare");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const t = text[language];
  const topIssues = useMemo(() => scan.issues.slice(0, 6), [scan.issues]);
  const output = scan.generated[activeOutput];

  async function runScan(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ url: targetUrl });
      const response = await fetch(`/api/scan?${params.toString()}`);
      const payload = (await response.json()) as HeaderScan | { error?: string };
      if (!response.ok) {
        throw new Error("error" in payload ? payload.error ?? t.error : t.error);
      }
      setScan(payload as HeaderScan);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t.error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="studio-shell">
      <section className="workbench">
        <header className="topbar">
          <a data-google-vignette="false" className="brand-lock" href="/" aria-label="HeaderHarden Studio home">
            <LockKeyhole size={22} />
            <span>{t.product}</span>
          </a>
          <nav aria-label="Utility links">
            <a data-google-vignette="false" href="/about.html">About</a>
            <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          </nav>
          <button className="language-button" type="button" onClick={() => setLanguage(language === "en" ? "ko" : "en")}>
            <Languages size={17} />
            {language.toUpperCase()}
          </button>
        </header>

        <section className="command-surface">
          <div className="intro-copy">
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
          </div>

          <form className="scan-form" onSubmit={runScan}>
            <label>
              <span>{t.url}</span>
              <div className="input-wrap">
                <Globe2 size={18} />
                <input value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} spellCheck={false} />
              </div>
            </label>
            <div className="form-actions">
              <button className="secondary-button" type="button" onClick={() => setScan(sampleScan)}>
                <Sparkles size={17} />
                {t.sample}
              </button>
              <button className="primary-button" type="submit" disabled={loading}>
                {loading ? <RefreshCw className="spin" size={17} /> : <Search size={17} />}
                {loading ? t.scanning : t.scan}
              </button>
            </div>
          </form>

          <p className="privacy-line">
            <ShieldCheck size={16} />
            {t.noLogin}
          </p>
        </section>

        {error ? (
          <div className="error-banner" role="alert">
            <AlertTriangle size={18} />
            {error}
          </div>
        ) : null}

        <section className="score-strip" aria-label="Scan summary">
          <ScoreDial scan={scan} label={t.score} />
          <SummaryTile label={t.status} value={scan.fetch.status ? String(scan.fetch.status) : "N/A"} detail={scan.fetch.finalUrl} level={scan.fetch.ok ? "pass" : "fail"} />
          <SummaryTile label="Pass" value={String(scan.summary.passes)} detail="clean checks" level="pass" />
          <SummaryTile label="Warn" value={String(scan.summary.warnings)} detail="review items" level={scan.summary.warnings ? "warn" : "pass"} />
          <SummaryTile label="Fail" value={String(scan.summary.failures)} detail="fix first" level={scan.summary.failures ? "fail" : "pass"} />
        </section>

        <section className="main-grid">
          <div className="left-column">
            <Panel title={t.checks} icon={<ShieldCheck size={18} />}>
              <CheckMatrix checks={scan.checks} />
            </Panel>

            <Panel title={t.csp} icon={<FileCode2 size={18} />}>
              <CspMap scan={scan} />
            </Panel>

            <Panel title={t.raw} icon={<Table2 size={18} />}>
              <RawHeaders headers={scan.headers} />
            </Panel>
          </div>

          <div className="right-column">
            <Panel title={t.output} icon={<Clipboard size={18} />}>
              <div className="tab-row" role="tablist" aria-label="Generated output">
                {copyTabs.map((tab) => (
                  <button type="button" key={tab.key} className={activeOutput === tab.key ? "active" : ""} onClick={() => setActiveOutput(tab.key)}>
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="code-output">
                <pre>{output}</pre>
                <CopyButton value={output} copyLabel={t.copy} copiedLabel={t.copied} />
              </div>
            </Panel>

            <Panel title={t.issues} icon={<AlertTriangle size={18} />}>
              <IssueQueue issues={topIssues} />
            </Panel>

            <aside className="cloudflare-note">
              <div>
                <RadioTower size={18} />
                <strong>{t.noteTitle}</strong>
              </div>
              <p>{t.noteBody}</p>
            </aside>

            <figure className="visual-asset">
              <img src="/headerharden-console.png" alt={t.consoleAlt} />
            </figure>
          </div>
        </section>
      </section>
    </main>
  );
}

function ScoreDial({ scan, label }: { scan: HeaderScan; label: string }) {
  const level = scan.score >= 75 ? "pass" : scan.score >= 55 ? "warn" : "fail";
  return (
    <article className={`score-dial ${level}`}>
      <div className="dial-ring" style={{ "--score": `${scan.score}%` } as React.CSSProperties}>
        <span>{scan.score}</span>
      </div>
      <div>
        <p>{label}</p>
        <strong>{scan.scoreLabel}</strong>
        <small>{scan.hostname}</small>
      </div>
    </article>
  );
}

function SummaryTile({ label, value, detail, level }: { label: string; value: string; detail: string; level: Level }) {
  return (
    <article className={`summary-tile ${level}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function Panel({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="panel">
      <div className="panel-title">
        {icon}
        <h2>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function CheckMatrix({ checks }: { checks: HeaderCheck[] }) {
  return (
    <div className="check-matrix">
      {checks.map((check) => (
        <article className={`check-row ${check.level}`} key={check.key}>
          <LevelIcon level={check.level} />
          <div>
            <strong>{check.label}</strong>
            <span>{check.value}</span>
          </div>
          <p>{check.detail}</p>
          <b>{check.points}</b>
        </article>
      ))}
    </div>
  );
}

function CspMap({ scan }: { scan: HeaderScan }) {
  if (!scan.csp.present && !scan.csp.reportOnly) {
    return (
      <div className="empty-state">
        <ShieldX size={24} />
        <strong>No enforced CSP detected</strong>
        <p>Add Content-Security-Policy to control scripts, frames, objects, forms, and base URLs.</p>
      </div>
    );
  }

  return (
    <div className="csp-map">
      {scan.csp.riskyTokens.length ? (
        <div className="risk-strip">
          <AlertTriangle size={16} />
          <span>{scan.csp.riskyTokens.join(", ")}</span>
        </div>
      ) : null}
      {scan.csp.directives.map((directive) => (
        <article className={`directive ${directive.level}`} key={`${directive.name}-${directive.value}`}>
          <span>{directive.name}</span>
          <strong>{directive.value}</strong>
          <p>{directive.detail}</p>
        </article>
      ))}
    </div>
  );
}

function RawHeaders({ headers }: { headers: HeaderPair[] }) {
  return (
    <div className="raw-table">
      {headers.slice(0, 22).map((header) => (
        <React.Fragment key={header.name}>
          <span>{header.name}</span>
          <code>{header.value}</code>
        </React.Fragment>
      ))}
    </div>
  );
}

function IssueQueue({ issues }: { issues: ScanIssue[] }) {
  if (!issues.length) {
    return (
      <div className="empty-state compact">
        <CheckCircle2 size={22} />
        <strong>No fail or warning items</strong>
        <p>The scanned response passed the main checks.</p>
      </div>
    );
  }

  return (
    <div className="issue-list">
      {issues.map((issue) => (
        <article className={`issue ${issue.level}`} key={`${issue.header}-${issue.title}`}>
          <LevelIcon level={issue.level} />
          <div>
            <strong>{issue.title}</strong>
            <span>{issue.header}</span>
            <p>{issue.detail}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

function LevelIcon({ level }: { level: Level }) {
  if (level === "pass") return <CheckCircle2 size={18} aria-label="pass" />;
  if (level === "fail") return <ShieldX size={18} aria-label="fail" />;
  if (level === "warn") return <AlertTriangle size={18} aria-label="warning" />;
  return <RadioTower size={18} aria-label="info" />;
}

function CopyButton({ value, copyLabel, copiedLabel }: { value: string; copyLabel: string; copiedLabel: string }) {
  const [copied, setCopied] = useState(false);

  async function copyValue() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  return (
    <button className="copy-button" type="button" onClick={copyValue}>
      <Copy size={16} />
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
